import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";

export const SUPPORTED_LANGUAGES = [
  "en", "es", "ar", "fr", "pt", "de", "nl", "hr",
  "fa", "cs", "no", "sv", "ko", "ja", "tr", "uz",
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const RTL_LANGUAGES: SupportedLanguage[] = ["ar", "fa"];

export const LANGUAGE_CONFIG: Record<SupportedLanguage, { name: string; flag: string; dir: "ltr" | "rtl" }> = {
  en: { name: "English", flag: "\u{1F1EC}\u{1F1E7}", dir: "ltr" },
  es: { name: "Espa\u00f1ol", flag: "\u{1F1EA}\u{1F1F8}", dir: "ltr" },
  ar: { name: "\u0627\u0644\u0639\u0631\u0628\u064A\u0629", flag: "\u{1F1F8}\u{1F1E6}", dir: "rtl" },
  fr: { name: "Fran\u00e7ais", flag: "\u{1F1EB}\u{1F1F7}", dir: "ltr" },
  pt: { name: "Portugu\u00eas", flag: "\u{1F1E7}\u{1F1F7}", dir: "ltr" },
  de: { name: "Deutsch", flag: "\u{1F1E9}\u{1F1EA}", dir: "ltr" },
  nl: { name: "Nederlands", flag: "\u{1F1F3}\u{1F1F1}", dir: "ltr" },
  hr: { name: "Hrvatski", flag: "\u{1F1ED}\u{1F1F7}", dir: "ltr" },
  fa: { name: "\u0641\u0627\u0631\u0633\u06CC", flag: "\u{1F1EE}\u{1F1F7}", dir: "rtl" },
  cs: { name: "\u010Ce\u0161tina", flag: "\u{1F1E8}\u{1F1FF}", dir: "ltr" },
  no: { name: "Norsk", flag: "\u{1F1F3}\u{1F1F4}", dir: "ltr" },
  sv: { name: "Svenska", flag: "\u{1F1F8}\u{1F1EA}", dir: "ltr" },
  ko: { name: "\uD55C\uAD6D\uC5B4", flag: "\u{1F1F0}\u{1F1F7}", dir: "ltr" },
  ja: { name: "\u65E5\u672C\u8A9E", flag: "\u{1F1EF}\u{1F1F5}", dir: "ltr" },
  tr: { name: "T\u00fcrk\u00e7e", flag: "\u{1F1F9}\u{1F1F7}", dir: "ltr" },
  uz: { name: "O\u02BBzbekcha", flag: "\u{1F1FA}\u{1F1FF}", dir: "ltr" },
};

// Custom i18next language detector that reads locale from the URL path.
// e.g. /es/faq -> "es", /ar/ -> "ar", /faq -> "en" (default)
const urlPathDetector = {
  name: "urlPath",
  lookup(): string | undefined {
    if (typeof window === "undefined") return undefined;
    const segments = window.location.pathname.split("/").filter(Boolean);
    if (segments.length > 0) {
      const first = segments[0];
      if ((SUPPORTED_LANGUAGES as readonly string[]).includes(first) && first !== "en") {
        return first;
      }
    }
    return "en";
  },
  cacheUserLanguage(): void {
    // URL is source of truth; no caching needed from this detector
  },
};

// Register the custom detector
const languageDetector = new LanguageDetector();
languageDetector.addDetector(urlPathDetector);

i18n
  .use(HttpBackend)
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    backend: {
      loadPath: "/locales/{{lng}}/translation.json",
    },
    detection: {
      order: ["urlPath", "localStorage", "navigator"],
      lookupLocalStorage: "wc_language",
      caches: ["localStorage"],
    },
    react: {
      useSuspense: true,
    },
  });

// Apply RTL/LTR and lang attribute on language change
i18n.on("languageChanged", (lng) => {
  const lang = lng as SupportedLanguage;
  const dir = LANGUAGE_CONFIG[lang]?.dir ?? "ltr";
  document.documentElement.lang = lang;
  document.documentElement.dir = dir;
});

export default i18n;
